'use client'

import * as amplitude from '@amplitude/analytics-browser'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import {
	extractErrorOrUnknown,
	formatPrefixedMessage,
} from '@/app/lib/errors/errorUtils'

/**
 * Amplitude Guides & Surveys Manager
 *
 * This module handles the initialization and management of Amplitude Guides SDK.
 * It provides utilities for booting the engagement SDK, tracking guide interactions,
 * and managing user cohort targeting.
 *
 * The engagement SDK is loaded via a <Script> tag in the layout and requires:
 * 1. The plugin to be added to Amplitude (done in initAmplitudeOnce)
 * 2. The boot() call with user identity and properties (done here)
 * 3. Event forwarding setup to track guide interactions (done here)
 */

interface UserProperties {
	user_id: string
	device_id?: string
	user_properties: Record<string, unknown>
	cohort?: string
}

/**
 * Checks if the engagement SDK is available in the window object
 */
export function isEngagementSdkAvailable(): boolean {
	return typeof window !== 'undefined' && !!window.engagement
}

// Track boot state globally to persist across HMR/hot reloads
const BOOT_STATE_KEY = '__AMPLITUDE_ENGAGEMENT_BOOTED__'
const BOOT_USER_KEY = '__AMPLITUDE_ENGAGEMENT_USER_ID__'
const SURVEY_CACHE_KEY = '__AMPLITUDE_ENGAGEMENT_SURVEY_CACHE__'

const reportEngagementError = (context: string, error: unknown): Error => {
	const normalized = extractErrorOrUnknown(
		error,
		context,
		context,
		'ENGAGEMENT'
	)
	const message =
		formatPrefixedMessage(context, normalized.message) ?? normalized.message
	pushToDataLayer(
		{
			event: 'Engagement SDK Error',
			eventParameter: context,
			eventParameterValue: message,
		},
		true
	)
	return normalized
}

const getWindowRecord = (): Record<string, unknown> | null => {
	if (typeof window === 'undefined') return null
	return window as unknown as Record<string, unknown>
}

const getBootState = () => {
	const record = getWindowRecord()
	if (!record) return { isBooted: false, userId: null }
	return {
		isBooted: !!record[BOOT_STATE_KEY],
		userId: (record[BOOT_USER_KEY] as string | null) ?? null,
	}
}

const setBootState = (isBooted: boolean, userId: string | null) => {
	const record = getWindowRecord()
	if (!record) return
	record[BOOT_STATE_KEY] = isBooted
	record[BOOT_USER_KEY] = userId
}

const getCachedSurveys = (): unknown[] => {
	if (typeof window === 'undefined') return []
	try {
		const raw = window.localStorage.getItem(SURVEY_CACHE_KEY)
		if (!raw) return []
		const parsed = JSON.parse(raw) as unknown
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

const setCachedSurveys = (surveys: unknown[]): void => {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.setItem(SURVEY_CACHE_KEY, JSON.stringify(surveys))
	} catch {
		// Ignore storage errors (e.g., disabled storage)
	}
}

const isSurveyLike = (item: unknown): item is Record<string, unknown> => {
	if (!item || typeof item !== 'object') return false
	const record = item as Record<string, unknown>
	const type = record.type ?? record.kind ?? record.entityType
	return typeof type === 'string' && type.toLowerCase() === 'survey'
}

const extractSurveysFromPayload = (payload: unknown): unknown[] => {
	if (Array.isArray(payload)) {
		return payload.filter(isSurveyLike)
	}
	if (!payload || typeof payload !== 'object') return []
	const record = payload as Record<string, unknown>
	const { surveys, items } = record
	if (Array.isArray(surveys)) return surveys
	if (Array.isArray(items)) return items.filter(isSurveyLike)
	return []
}

const updateSurveyCacheFromGuidesApi = (): void => {
	const guidesApi = window.engagement?.gs as
		| { getAllGuidesAndSurveys?: () => unknown }
		| undefined
	const fallbackList = guidesApi?.getAllGuidesAndSurveys?.() ?? null
	if (!fallbackList) return

	if (fallbackList instanceof Promise) {
		fallbackList
			.then(payload => {
				const extracted = extractSurveysFromPayload(payload)
				if (extracted.length > 0) {
					setCachedSurveys(extracted)
				}
			})
			.catch(() => {
				// Ignore fallback errors and rely on cache
			})
		return
	}

	const extracted = extractSurveysFromPayload(fallbackList)
	if (extracted.length > 0) {
		setCachedSurveys(extracted)
	}
}

const safeUpdateSurveyCache = (): void => {
	try {
		updateSurveyCacheFromGuidesApi()
	} catch (error) {
		console.warn('Survey cache update failed:', error)
	}
}

const waitForSurveysReady = (): Promise<void> => {
	const timeoutMs = 10000

	return new Promise(resolve => {
		const start = Date.now()
		const intervalId = setInterval(() => {
			const surveysReady =
				typeof window !== 'undefined' &&
				typeof window.engagement?.surveys?.show === 'function'
			if (surveysReady) {
				clearInterval(intervalId)
				resolve()
				return
			}

			if (Date.now() - start >= timeoutMs) {
				clearInterval(intervalId)
				resolve()
			}
		}, 200)
	})
}

/**
 * Sets up event forwarding to track guide interactions
 * This ensures that guide view, dismiss, and click events are tracked in Amplitude Analytics
 */
function setupGuideEventForwarding(): void {
	if (!isEngagementSdkAvailable()) {
		return
	}

	// Add a listener for guide events (if available in SDK version)
	// This tracks when users see, dismiss, or interact with guides
	if (!window.engagement?.on || typeof window.engagement.on !== 'function') {
		return
	}

	try {
		window.engagement.on('guide:shown', () => {})
		window.engagement.on('guide:dismissed', () => {})
		window.engagement.on('guide:clicked', () => {})
	} catch (error) {
		console.warn('Could not setup guide event forwarding:', error)
	}
}

const markBooted = (userId: string): void => {
	setBootState(true, userId)
	setupGuideEventForwarding()
	safeUpdateSurveyCache()
}

const bootIfAvailable = (userProps: UserProperties): void => {
	if (!window.engagement?.boot) return
	window.engagement.boot({
		user: {
			user_id: userProps.user_id,
			device_id: userProps.device_id,
			user_properties: userProps.user_properties,
		},
		integrations: [amplitude],
	})
}

const getErrorMessage = (error: unknown): string => {
	if (error instanceof Error) return error.message
	if (typeof error === 'string') return error
	if (error && typeof error === 'object' && 'message' in error) {
		const { message } = error as { message?: unknown }
		return typeof message === 'string' ? message : ''
	}
	return ''
}

const isAlreadyBootedError = (bootError: unknown): boolean => {
	const errorMsg = getErrorMessage(bootError)
	return (
		errorMsg.includes('User already booted') ||
		errorMsg.includes('already booted')
	)
}

/**
 * Boots the Amplitude Engagement SDK with user identity and properties
 * This must be called after initAmplitudeOnce() to enable guide targeting
 *
 * Errors are logged and tracked to analytics but NOT thrown.
 * This follows graceful degradation patterns - guides are a non-critical feature
 * and should not break the application initialization flow.
 *
 * @param userProps - User properties including user_id, device_id, and custom properties
 * @example
 * bootAmplitudeEngagement({
 *   user_id: 'user_12345',
 *   device_id: 'device_xyz',
 *   user_properties: {
 *     cohort: 'beta_testers',
 *     plan_type: 'premium',
 *     company_id: 'comp_123'
 *   },
 *   cohort: 'beta_testers'
 * })
 */
export async function bootAmplitudeEngagement(
	userProps: UserProperties
): Promise<void> {
	// Check if already booted for this user
	const { isBooted, userId } = getBootState()
	if (isBooted && userId === userProps.user_id) {
		safeUpdateSurveyCache()
		return
	}
	let bootResolved = false
	let timeoutId: ReturnType<typeof setTimeout> | null = null

	const pollSdk = (): Promise<void> => {
		return new Promise((resolve, reject) => {
			const checkSdk = () => {
				if (!isEngagementSdkAvailable()) {
					setTimeout(checkSdk, 100)
					return
				}

				try {
					bootIfAvailable(userProps)
					markBooted(userProps.user_id)
					bootResolved = true
					resolve()
				} catch (bootError: unknown) {
					if (isAlreadyBootedError(bootError)) {
						markBooted(userProps.user_id)
						bootResolved = true
						resolve()
						return
					}
					reject(
						reportEngagementError(
							'Amplitude Guides & Surveys boot failed',
							bootError
						)
					)
				}
			}
			checkSdk()
		})
	}

	const timeoutPromise = new Promise<void>((_, reject) => {
		timeoutId = setTimeout(() => {
			if (!bootResolved) {
				reject(
					new Error(
						'Amplitude Guides & Surveys SDK failed to boot: engagement SDK not available after 15 seconds'
					)
				)
			}
		}, 15000)
	})

	try {
		await Promise.race([pollSdk(), timeoutPromise])
		await waitForSurveysReady()
	} catch (error) {
		// Log and track error for debugging, but don't throw
		// This is a non-critical feature that should fail gracefully
		reportEngagementError('Amplitude Guides & Surveys boot error', error)
		console.warn('Amplitude Guides & Surveys init failed (graceful degradation):', error)
	} finally {
		if (timeoutId) {
			clearTimeout(timeoutId)
		}
	}
}

/**
 * Manually trigger/show a specific guide by key
 * Useful for testing or showing guides programmatically
 *
 * @param guideKey - The unique key/identifier of the guide in Amplitude
 * @example
 * showGuide('onboarding_guide_v1')
 */
export function showGuide(guideKey: string): void {
	if (!isEngagementSdkAvailable()) {
		console.warn('Guides SDK not available')
		return
	}

	try {
		if (window.engagement?.gs?.show) {
			window.engagement.gs.show(guideKey)
		}
	} catch (error) {
		reportEngagementError(`Failed to show guide "${guideKey}"`, error)
	}
}

/**
 * Close all open guides
 */
export function closeAllGuides(): void {
	if (!isEngagementSdkAvailable()) {
		return
	}

	try {
		if (window.engagement?.gs?.closeAll) {
			window.engagement.gs.closeAll()
		}
	} catch (error) {
		reportEngagementError('Failed to close guides', error)
	}
}

/**
 * Get the list of available guides
 * Useful for debugging and testing
 */
export function getAvailableGuides(): unknown[] {
	if (!isEngagementSdkAvailable()) {
		return []
	}

	try {
		return window.engagement?.gs?.list?.() ?? []
	} catch (error) {
		reportEngagementError('Failed to get guides list', error)
		return []
	}
}

/**
 * Manually trigger/show a specific survey by key
 * Useful for testing or showing surveys programmatically
 *
 * @param surveyKey - The unique key/identifier of the survey in Amplitude
 * @example
 * showSurvey('nps_survey_v1')
 */
export function showSurvey(surveyKey: string): void {
	if (!isEngagementSdkAvailable()) {
		console.warn('Surveys SDK not available')
		return
	}

	try {
		if (window.engagement?.surveys?.show) {
			window.engagement.surveys.show(surveyKey)
		}
	} catch (error) {
		reportEngagementError(`Failed to show survey "${surveyKey}"`, error)
	}
}

/**
 * Close the currently open survey
 */
export function closeSurvey(): void {
	if (!isEngagementSdkAvailable()) {
		return
	}

	try {
		if (window.engagement?.surveys?.close) {
			window.engagement.surveys.close()
		}
	} catch (error) {
		reportEngagementError('Failed to close survey', error)
	}
}

/**
 * Get the list of available surveys
 * Useful for debugging and testing
 */
export function getAvailableSurveys(): unknown[] {
	if (!isEngagementSdkAvailable()) {
		return []
	}

	try {
		const directList = window.engagement?.surveys?.list?.() ?? []
		if (directList.length > 0) {
			setCachedSurveys(directList)
			return directList
		}

		safeUpdateSurveyCache()

		return getCachedSurveys()
	} catch (error) {
		reportEngagementError('Failed to get surveys list', error)
		return getCachedSurveys()
	}
}

/**
 * Debug helper to check the status of the engagement SDK
 * Outputs connection status and targeting information
 */
export function debugEngagementStatus(): void {
	if (!isEngagementSdkAvailable()) {
		return
	}

	try {
		if (window.engagement?._debugStatus) {
			window.engagement._debugStatus()
		}
	} catch (error) {
		reportEngagementError('Debug status failed', error)
	}
}
